import { Component, DestroyRef, effect, inject, input, LOCALE_ID, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PharmacyVTO } from '../../../../services/pharmacy/pharmacy.model';
import { ApprovePharmacyDialog } from '../../../admin-dashboard-component/pharmacies/dialogs/approve-pharmacy-dialog/approve-pharmacy-dialog';
import { SafeUrl } from '@angular/platform-browser';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';

@Component({
  selector: 'app-view-pharmacy-details',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './view-pharmacy-details.html',
  styleUrl: './view-pharmacy-details.scss',
})
export class ViewPharmacyDetails {
  data = input.required<PharmacyVTO>();
  isAdmin = input<boolean>(false);

  private localeId = inject(LOCALE_ID);
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);

  showApproveDialog = signal<boolean>(false);
  previewImageUrl = signal<SafeUrl | null>(null);
  isLoadingFile = signal<boolean>(false);
  fileErrorMessage = signal<string | null>(null);
  fileTypes = signal<Record<string, 'image' | 'pdf'>>({});

  constructor() {
    effect(() => {
      const d = this.data();
      const fids = [
        d.commercialRegistrationFid,
        d.taxRegistrationCertificateFid,
        d.pharmacyContractFid,
        d.pharmacyLicenseFid,
        d.pharmacistNationalIdFid,
      ].filter(Boolean) as string[];

      fids.forEach((fid) => {
        if (this.fileTypes()[fid]) return;
        this.fileMgt.getImageUrl(fid).subscribe({
          next: (res) => {
            this.fileTypes.update((types) => ({
              ...types,
              [fid]: res.isImage ? 'image' : 'pdf',
            }));
          },
          error: () => {
            this.fileTypes.update((types) => ({ ...types, [fid]: 'pdf' }));
          },
        });
      });
    });
  }

  getTranslatedTitle(obj: any): string {
    if (!obj) return '';
    return this.localeId.startsWith('ar') ? obj.titleAr : obj.titleEn;
  }

  openApproveDialog() {
    this.showApproveDialog.set(true);
  }

  onApproveSuccess() {
    this.showApproveDialog.set(false);
  }

  closePreview() {
    this.previewImageUrl.set(null);
  }

  private getErrorMessage(err: any, arFallback: string, enFallback: string): string {
    return this.localeId.startsWith('ar')
      ? err?.messageAr?.trim() || err?.messageEn || arFallback
      : err?.messageEn || enFallback;
  }

  viewFile(fid: string) {
    if (!fid) return;

    this.isLoadingFile.set(true);
    this.fileErrorMessage.set(null);

    const subscription = this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.isLoadingFile.set(false);
        this.fileTypes.update((types) => ({
          ...types,
          [fid]: res.isImage ? 'image' : 'pdf',
        }));
        if (res.isImage) {
          this.previewImageUrl.set(res.url);
        } else {
          window.open(res.url as string, '_blank');
        }
      },
      error: (err) => {
        this.isLoadingFile.set(false);
        this.fileErrorMessage.set(
          this.getErrorMessage(err, 'الملف غير متاح أو لا يمكن تحميله', 'File not available or could not be loaded'),
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  downloadFile(fid: string) {
    if (!fid) return;

    this.fileErrorMessage.set(null);

    this.fileMgt.getImage(fid).subscribe({
      next: (blob: Blob) => {
        const ext = this.fileTypes()[fid] === 'pdf' ? 'pdf' : 'png';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `file_${fid}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.fileErrorMessage.set(
          this.getErrorMessage(err, 'الملف غير متاح للتحميل', 'File not available for download'),
        );
      },
    });
  }
}
